/**
 * client_id idempotency for paid DM sends (FRONT-308).
 *
 * BE contract (api-direct-messages.md §Idempotency): one UUID per send tap,
 * persisted until success or abort; retries after network errors MUST reuse
 * the same UUID (`UNIQUE(client_id)` collapses duplicates to one charge);
 * never reuse a UUID for a different message.
 *
 * The pending id is bound to the exact body: a timeout may have committed the
 * charge server-side, and retrying the stored UUID returns that original
 * message as `deduplicated: true`. Reusing it after the user edited the text
 * would silently "send" the stale body — an edited body gets a fresh UUID.
 */

import { captureError } from '@/shared/lib/errors/capture';

const STORAGE_KEY = 'tabootv_dm_pending_sends';

type PendingSends = Record<string, { uuid: string; body: string }>;

function readPending(): PendingSends {
  if (typeof window === 'undefined') return {};
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : {};
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    return parsed as PendingSends;
  } catch (error) {
    captureError(error, {
      component: 'DmClientId',
      action: 'read',
      severity: 'info',
      skipPostHog: true,
    });
    return {};
  }
}

function writePending(pending: PendingSends): void {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(pending));
  } catch (error) {
    captureError(error, {
      component: 'DmClientId',
      action: 'write',
      severity: 'info',
      skipPostHog: true,
    });
  }
}

/**
 * UUID for this send attempt. Call from the send event handler only — never at
 * render time (Cache Components bans render-time randomness). Reuses the
 * pending UUID only when the body is byte-identical to the failed attempt.
 */
export function getOrCreateSendClientId(channelUuid: string, body: string): string {
  const pending = readPending();
  const existing = pending[channelUuid];
  if (existing && existing.body === body) return existing.uuid;

  const uuid = crypto.randomUUID();
  pending[channelUuid] = { uuid, body };
  writePending(pending);
  return uuid;
}

/** Forget the pending send after success, dedup, or user abort. */
export function clearSendClientId(channelUuid: string): void {
  const pending = readPending();
  if (!(channelUuid in pending)) return;
  delete pending[channelUuid];
  writePending(pending);
}
